import { type ReactNode, useState } from "react";
import { MoreVertical, Pencil, Trash2 } from "lucide-react";
import { AppBottomSheetSurface, AppButton, AppDialogSurface, AppIconButton } from "../../ui-foundation";
import { DEFAULT_REACTION_OPTIONS, buildReactionMenuLabel } from "../../shared/community";

export type MessageAction = {
  id: string;
  label: string;
  icon?: ReactNode;
  tone?: "outline" | "secondary" | "danger" | "ghost";
  onSelect: () => void | Promise<void>;
};

interface MessageActionsProps {
  onEdit?: () => void;
  onDelete?: () => void;
  deleteLabel?: string;
  showConfirm?: boolean;
  showEdit?: boolean;
  actions?: MessageAction[];
  menuTitle?: string;
  menuDescription?: string;
  triggerLabel?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  selectedReaction?: string;
  onReactionSelect?: (emoji: string) => void | Promise<void>;
}

const MessageActions = ({
  onEdit,
  onDelete,
  deleteLabel = "Delete",
  showConfirm = true,
  showEdit = true,
  actions = [],
  menuTitle = "Message actions",
  menuDescription = "Choose what to do with this message.",
  triggerLabel = "More message actions",
  open,
  onOpenChange,
  selectedReaction,
  onReactionSelect,
}: MessageActionsProps) => {
  const [internalMenuOpen, setInternalMenuOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const menuOpen = open ?? internalMenuOpen;
  const setMenuOpen = (nextOpen: boolean) => {
    if (open === undefined) {
      setInternalMenuOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  };

  return (
    <>
      <AppIconButton label={triggerLabel} tone="ghost" size="sm" onPress={() => setMenuOpen(true)}>
        <MoreVertical size={14} />
      </AppIconButton>

      <AppBottomSheetSurface
        open={menuOpen}
        onOpenChange={setMenuOpen}
        title={menuTitle}
        description={menuDescription}
        maxWidthClassName="max-w-md"
      >
        <div className="space-y-2">
          {onReactionSelect ? (
            <div
              aria-label="Quick reactions"
              className="mb-4 flex items-center justify-between gap-1 rounded-full border border-border bg-background px-2 py-2 shadow-sm"
            >
              {DEFAULT_REACTION_OPTIONS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  title={buildReactionMenuLabel(emoji)}
                  aria-label={buildReactionMenuLabel(emoji)}
                  aria-pressed={selectedReaction === emoji}
                  onClick={() => {
                    setMenuOpen(false);
                    void onReactionSelect(emoji);
                  }}
                  className={`inline-flex h-11 w-11 items-center justify-center rounded-full text-xl transition-colors hover:bg-primary/10 ${
                    selectedReaction === emoji ? "bg-primary/15 ring-2 ring-primary/25" : ""
                  }`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          ) : null}
          {actions.map((action) => (
            <AppButton
              key={action.id}
              type="button"
              tone={action.tone ?? "outline"}
              width="100%"
              onPress={() => {
                setMenuOpen(false);
                void action.onSelect();
              }}
            >
              {action.icon}
              {action.label}
            </AppButton>
          ))}
          {showEdit && onEdit ? (
            <AppButton
              type="button"
              tone="outline"
              width="100%"
              onPress={() => {
                setMenuOpen(false);
                onEdit();
              }}
            >
              <Pencil size={14} />
              Edit
            </AppButton>
          ) : null}
          {onDelete ? (
            <AppButton
              type="button"
              tone="danger"
              width="100%"
              onPress={() => {
                setMenuOpen(false);
                if (showConfirm) {
                  setConfirmOpen(true);
                  return;
                }
                onDelete();
              }}
            >
              <Trash2 size={14} />
              {deleteLabel}
            </AppButton>
          ) : null}
        </div>
      </AppBottomSheetSurface>

      <AppDialogSurface
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={`${deleteLabel}?`}
        description="This cannot be undone."
        maxWidthClassName="max-w-md"
        footer={
          <>
            <AppButton type="button" tone="outline" onPress={() => setConfirmOpen(false)}>
              Cancel
            </AppButton>
            <AppButton
              type="button"
              tone="danger"
              onPress={() => {
                onDelete?.();
                setConfirmOpen(false);
              }}
            >
              {deleteLabel}
            </AppButton>
          </>
        }
      />
    </>
  );
};

export default MessageActions;
